import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Select, Row, Input, Checkbox, DatePicker } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  getCustomers,
  clearCustomers,
  getActiveJobchartIds,
  clearActiveJobchartIds,
  clearUsers,
  getUsers,
  getColorIds,
  clearColorIds,
  clearUnitIds,
  getUnitIds,
  getFabricsForJobchart,
  clearFabricsForJobchart,
  getAccessoriesForJobchart,
  clearAccessoriesForJobchart,
  getJobchartFabricsForRpo,
  clearJobchartFabricsForRpo,
  getJobchartAccessoriesForRpo,
  clearJobchartAccessoriesForRpo,
  getJobchartStylesForRpo,
  clearJobchartStylesForRpo,
  getVendors,
  clearVendors,
  getNewRPONumber,
  clearNewRPONumber,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import constants from '../constants';
import { canAuthorizeRPO } from './rpoAuthorizers';
import FabricForm from './FabricForm';
import AccessoryForm from './AccessoryForm';
import ItemForm from './ItemForm';
import TermForm from './TermForm';
import ProductFabricForm from './ProductFabricForm';
import ProductAccessoryForm from './ProductAccessoryForm';
import ProductStyleForm from './ProductStyleForm';
import { getGrandTotal } from './common-functions';
import { get } from '../utils.js';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntDatePicker = GetField(DatePicker);
const AntInputTextArea = GetField(Input.TextArea);
const AntCheckbox = GetField(Checkbox);

const CreateRenukaPO = props => {
  const { classes } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  const { handleSubmit, control, getValues, watch, setValue } = useForm({
    shouldUnregister: false,
    defaultValues: {
      fabrics: [{ productId: null, fabricId: null }],
      accessories: [{ productId: null, accessoryId: null }],
      items: [{ name: null }],
      styles: [
        {
          productId: null,
          styleId: null,
          baseColorId: null,
          printColorId: null,
          embroideryColorId: null,
          acidWashColorId: null,
        },
      ],
      terms: [{ term: null }],
    },
  });

  const {
    fields: fabricFields,
    append: fabricAppend,
    remove: fabricRemove,
  } = useFieldArray({
    control,
    name: 'fabrics',
  });

  const {
    fields: accessoryFields,
    append: accessoryAppend,
    remove: accessoryRemove,
  } = useFieldArray({
    control,
    name: 'accessories',
  });

  const {
    fields: itemFields,
    append: itemAppend,
    remove: itemRemove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const {
    fields: styleFields,
    append: styleAppend,
    remove: styleRemove,
  } = useFieldArray({
    control,
    name: 'styles',
  });
  const {
    fields: termFields,
    append: termAppend,
    remove: termRemove,
  } = useFieldArray({
    control,
    name: 'terms',
  });

  const bpoNo = useWatch({ control, name: 'jobchartId' });
  const isJobOrderBool = useWatch({ control, name: 'isJobOrder' });

  const newRenukaPONo = useSelector(state => state.newRenukaPONo);

  const users = useSelector(state => state.users);

  const vendors = useSelector(state => state.vendors);

  const customers = useSelector(state => state.customers);

  const activeJobchartIds = useSelector(state => state.activeJobchartIds);

  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    dispatch(getNewRPONumber());
    dispatch(getVendors());
    dispatch(getUsers());
    dispatch(getCustomers());
    dispatch(getColorIds());
    dispatch(getUnitIds());
    dispatch(getFabricsForJobchart());
    dispatch(getAccessoriesForJobchart());
    return () => {
      dispatch(clearNewRPONumber());
      dispatch(clearVendors());
      dispatch(clearUsers());
      dispatch(clearCustomers());
      dispatch(clearColorIds());
      dispatch(clearUnitIds());
      dispatch(clearFabricsForJobchart());
      dispatch(clearAccessoriesForJobchart());
    };
  }, []);

  useEffect(() => {
    if (newRenukaPONo) {
      setValue('rpoNo', newRenukaPONo);
    }
  }, [newRenukaPONo]);

  useEffect(() => {
    const customerId = getValues('customerId');
    if (!customerId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ customerId }));
    }
  }, [watch('customerId')]);

  useEffect(() => {
    if (bpoNo) {
      dispatch(getJobchartFabricsForRpo(bpoNo));
      dispatch(getJobchartAccessoriesForRpo(bpoNo));
    } else {
      dispatch(clearJobchartFabricsForRpo());
      dispatch(clearJobchartAccessoriesForRpo());
    }

    setValue('accessories', [{}]);
    setValue('fabrics', [{}]);
  }, [bpoNo]);

  useEffect(() => {
    if (bpoNo && isJobOrderBool) {
      dispatch(getJobchartStylesForRpo(bpoNo));
    } else {
      dispatch(clearJobchartStylesForRpo());
    }

    setValue('styles', [{}]);
  }, [bpoNo, isJobOrderBool]);

  useEffect(() => {
    const fabricItems = getValues('fabrics');
    const accessoryItems = getValues('accessories');
    const items = getValues('items');
    const stylesItems = getValues('styles');
    setValue(
      'grandTotal',
      getGrandTotal(fabricItems, accessoryItems, items, stylesItems)
    );
  }, [
    itemFields.length,
    fabricFields.length,
    accessoryFields.length,
    styleFields.length,
  ]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post('/purchase-orders', formValues);
      successNotification('Purchase order created');
      history.push('/renukapo/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Purchase Order"
        moduleLink="/renukapo/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  allowClear
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="jobchartId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="BPO #"
                  allowClear
                  placeholder="Select bpo"
                  {...field}
                >
                  {activeJobchartIds.map(jobchart => (
                    <Option key={jobchart.id} value={jobchart.id}>
                      {jobchart.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="vendorId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Vendor"
                  allowClear
                  placeholder="Select vendor"
                  {...field}
                >
                  {vendors.map(vendor => (
                    <Option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="rpoNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="RPO #"
                  disabled
                  placeholder="RPO #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="rpoDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  label="Date"
                  allowClear
                  placeholder="Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="authorizerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Authorizer"
                  allowClear
                  placeholder="Select authorizer"
                  {...field}
                >
                  {users
                    // HOTFIX 2026-10-29: only Prashant, Ankit and Narender can authorize an RPO (owner's decision).
                    // Old rule kept below for revert; to be replaced by an RBAC permission on dev.
                    // .filter(
                    //   user =>
                    //     user.id === 2 ||
                    //     user.id === 3 ||
                    //     user.id === 6 ||
                    //     user.id === 21 ||
                    //     user.id === 28 ||
                    //     user.id === 29 ||
                    //     user.id === 54
                    // )
                    .filter(user =>
                      canAuthorizeRPO(user.id, watch('vendorId'))
                    )
                    .map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.name}
                      </Option>
                    ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="isJobOrder"
              control={control}
              render={({ field }) => (
                <AntCheckbox label="Job Order" {...field} />
              )}
            />
          </LeftGrid>
          {isJobOrderBool ? (
            <RightGrid>
              <Controller
                name="process"
                control={control}
                render={({ field }) => (
                  <AntSelect
                    label="Process"
                    placeholder="Select process"
                    {...field}
                  >
                    {Object.values(constants.rpoProcesses).map(process => (
                      <Option key={process} value={process}>
                        {startCase(process)}
                      </Option>
                    ))}
                  </AntSelect>
                )}
              />
            </RightGrid>
          ) : null}
        </Row>
        <div className={classes.previewSubHeading}>Fabrics Table</div>
        <Row>
          <div className={classes.tableStyle}>
            <table className={classes.inputTable}>
              <thead className={classes.inputThead}>
                <tr key="header">
                  {bpoNo ? <th className={classes.inputTh}>Product</th> : null}
                  <th className={classes.inputTh}>Fabric</th>
                  <th className={classes.inputTh} style={{ width: '7%' }}>
                    Width
                  </th>
                  <th className={classes.inputTh} style={{ width: '7%' }}>
                    Base Color
                  </th>
                  <th className={classes.inputTh} style={{ width: '7%' }}>
                    Print Color
                  </th>
                  {bpoNo ? (
                    <th className={classes.inputTh} style={{ width: '6%' }}>
                      Jobchart Qty
                    </th>
                  ) : null}
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Qty
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Rate
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Value
                  </th>
                  <th className={classes.inputTh} style={{ width: '4%' }}>
                    GST (%)
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    GST Value
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Final Rate
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Total
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    HSN Code
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Delivery Date
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {fabricFields.map((item, index) =>
                  bpoNo ? (
                    <ProductFabricForm
                      key={item.id}
                      fabricItem={item}
                      fabricIndex={index}
                      fabricAppend={fabricAppend}
                      fabricRemove={fabricRemove}
                      getValues={getValues}
                      setValue={setValue}
                      control={control}
                      watch={watch}
                    />
                  ) : (
                    <FabricForm
                      key={item.id}
                      fabricItem={item}
                      fabricIndex={index}
                      fabricAppend={fabricAppend}
                      fabricRemove={fabricRemove}
                      getValues={getValues}
                      setValue={setValue}
                      control={control}
                      watch={watch}
                    />
                  )
                )}
              </tbody>
            </table>
          </div>
        </Row>
        <br />
        <div className={classes.previewSubHeading}>Accessories Table</div>
        <Row>
          <div className={classes.tableStyle}>
            <table className={classes.inputTable}>
              <thead className={classes.inputThead}>
                <tr key="header">
                  {bpoNo ? (
                    <th className={classes.inputTh} style={{ width: '15%' }}>
                      Product
                    </th>
                  ) : null}
                  <th className={classes.inputTh}>Accessory</th>
                  <th className={classes.inputTh} style={{ width: '4%' }}>
                    Unit
                  </th>
                  {bpoNo ? (
                    <th className={classes.inputTh} style={{ width: '6%' }}>
                      Jobchart Qty
                    </th>
                  ) : null}
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Qty
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Rate
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Value
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    GST (%)
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    GST Value
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Final Rate
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    Total
                  </th>
                  <th className={classes.inputTh} style={{ width: '5%' }}>
                    HSN Code
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Delivery Date
                  </th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {accessoryFields.map((item, index) =>
                  bpoNo ? (
                    <ProductAccessoryForm
                      key={item.id}
                      accessoryItem={item}
                      accessoryIndex={index}
                      accessoryAppend={accessoryAppend}
                      accessoryRemove={accessoryRemove}
                      getValues={getValues}
                      setValue={setValue}
                      control={control}
                      watch={watch}
                    />
                  ) : (
                    <AccessoryForm
                      key={item.id}
                      accessoryItem={item}
                      accessoryIndex={index}
                      accessoryAppend={accessoryAppend}
                      accessoryRemove={accessoryRemove}
                      getValues={getValues}
                      setValue={setValue}
                      control={control}
                      watch={watch}
                    />
                  )
                )}
              </tbody>
            </table>
          </div>
        </Row>
        <br />
        {permissions.includes(constants.permissions.itemTableInrenukaPO) ? (
          <>
            <div className={classes.previewSubHeading}>Items Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      <th className={classes.inputTh}>Item</th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Shade
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Size
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Unit
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Value
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        GST (%)
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        GST Value
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Final Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Total
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        HSN Code
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Delivery Date
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemFields.map((item, index) => (
                      <ItemForm
                        key={item.id}
                        itemItem={item}
                        itemIndex={index}
                        itemAppend={itemAppend}
                        itemRemove={itemRemove}
                        getValues={getValues}
                        setValue={setValue}
                        control={control}
                        watch={watch}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}

        {watch('isJobOrder') && watch('jobchartId') ? (
          <>
            <br />
            <div className={classes.previewSubHeading}>Products Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <div style={{ minWidth: '2300px' }}>
                  <table className={classes.inputTable}>
                    <thead className={classes.inputThead}>
                      <tr key="header">
                        <th className={classes.inputTh} style={{ width: '8%' }}>
                          Product
                        </th>
                        <th className={classes.inputTh}>Style</th>
                        <th className={classes.inputTh}>Description</th>
                        <th className={classes.inputTh}>Size</th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Base Color
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Print Color
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Embroidery Color
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Acid Wash Color
                        </th>
                        {bpoNo ? (
                          <th
                            className={classes.inputTh}
                            style={{ width: '6%' }}
                          >
                            Jobchart Qty
                          </th>
                        ) : null}
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Qty
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Unit
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Rate
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Value
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          GST (%)
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          GST Value
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Final Rate
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Total
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          HSN Code
                        </th>
                        <th className={classes.inputTh} style={{ width: '6%' }}>
                          Delivery Date
                        </th>
                        <th className={classes.inputTh} style={{ width: '4%' }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {styleFields.map((item, index) => (
                        <ProductStyleForm
                          key={item.id}
                          styleItem={item}
                          styleIndex={index}
                          styleAppend={styleAppend}
                          styleRemove={styleRemove}
                          getValues={getValues}
                          setValue={setValue}
                          control={control}
                          watch={watch}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Row>
          </>
        ) : null}
        <br />
        <div className={classes.previewSubHeading}>Terms Table</div>
        <Row>
          <div className={classes.tableStyle}>
            <table className={classes.inputTable}>
              <thead className={classes.inputThead}>
                <tr key="header">
                  <th className={classes.inputTh}>Term</th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {termFields.map((item, index) => (
                  <TermForm
                    key={item.id}
                    termItem={item}
                    termIndex={index}
                    termAppend={termAppend}
                    termRemove={termRemove}
                    control={control}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Row>
        <br />
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  label="Remark"
                  allowClear
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="grandTotal"
              control={control}
              render={({ field }) => (
                <AntInput
                  type="number"
                  disabled
                  label="Grand Total"
                  placeholder="Grand Total"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

CreateRenukaPO.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateRenukaPO);
